/**
 * Runs an async task for a burst of updates, coalescing them to the latest.
 *
 * Runs `run` for `initial`, then repeatedly for whatever `takeNext` returns —
 * which should return the latest pending value and clear it — until it returns
 * `null`. Because only the newest pending value is taken between runs, a burst
 * of updates that arrive while a run is in flight collapses to a single
 * follow-up run for the last one: none are run redundantly, and none are
 * dropped.
 *
 * The caller serialises entry (e.g. with an "is running" guard) so only one
 * drain is active at a time and updates during a run are funnelled into the
 * value `takeNext` reads.
 */
export const runCoalesced = async <T>(
  initial: T,
  takeNext: () => T | null,
  run: (value: T) => Promise<void>
): Promise<void> => {
  let next: T | null = initial;
  while (next !== null) {
    await run(next);
    next = takeNext();
  }
};
